import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: '0',
    max_uses: '',
    expires_at: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`);
      setCoupons(response.data);
    } catch (error) {
      toast.error('Erro ao carregar cupons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/coupons`, {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        min_purchase: parseFloat(formData.min_purchase),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      });
      toast.success('Cupom criado com sucesso!');
      setShowModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cupom');
    }
  };

  const toggleCoupon = async (couponId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/coupons/${couponId}`, {
        active: !currentStatus
      });
      toast.success(`Cupom ${!currentStatus ? 'ativado' : 'desativado'}!`);
      fetchCoupons();
    } catch (error) {
      toast.error('Erro ao atualizar cupom');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Tem certeza que deseja deletar este cupom?')) return;
    
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      toast.success('Cupom deletado!');
      fetchCoupons();
    } catch (error) {
      toast.error('Erro ao deletar cupom');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_purchase: '0',
      max_uses: '',
      expires_at: ''
    });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-coupons-page">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-heading text-4xl font-bold text-white">Cupons de Desconto</h1>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-primary text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition-colors flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Criar Cupom
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-16 w-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coupons.map((coupon) => (
              <div key={coupon.id} className="bg-zinc-900/50 border border-white/10 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-mono text-2xl font-bold text-primary">{coupon.code}</h3>
                    <p className="text-sm text-zinc-400 mt-1">
                      {coupon.discount_type === 'percentage' 
                        ? `${coupon.discount_value}% de desconto`
                        : `R$ ${coupon.discount_value.toFixed(2)} de desconto`
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => toggleCoupon(coupon.id, coupon.active)}
                    className={`p-2 rounded transition-colors ${
                      coupon.active 
                        ? 'text-primary hover:bg-primary/10'
                        : 'text-zinc-600 hover:bg-zinc-800'
                    }`}
                  >
                    {coupon.active ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                  </button>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  {coupon.min_purchase > 0 && (
                    <p className="text-zinc-400">Mínimo: R$ {coupon.min_purchase.toFixed(2)}</p>
                  )}
                  {coupon.max_uses && (
                    <p className="text-zinc-400">Usos: {coupon.uses_count}/{coupon.max_uses}</p>
                  )}
                  {coupon.expires_at && (
                    <p className="text-zinc-400">
                      Expira: {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => deleteCoupon(coupon.id)}
                    className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-500 py-2 flex items-center justify-center gap-2 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Deletar
                  </button>
                </div>

                {!coupon.active && (
                  <div className="mt-3 text-center text-xs text-zinc-600 uppercase tracking-wider">Desativado</div>
                )}
              </div>
            ))}
          </div>
        )}

        {coupons.length === 0 && !loading && (
          <div className="text-center py-20 text-zinc-400">
            Nenhum cupom criado ainda
          </div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowModal(false)}
                className="fixed inset-0 bg-black/80 z-50"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
              >
                <div className="bg-zinc-900 border border-white/10 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
                  <div className="p-6">
                    <h2 className="font-heading text-2xl font-bold text-white mb-6">Criar Cupom</h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Código *</label>
                        <input
                          type="text"
                          value={formData.code}
                          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                          className="w-full bg-zinc-800 border border-zinc-700 focus:border-primary h-12 px-4 text-white font-mono"
                          placeholder="DESCONTO10"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Tipo *</label>
                          <select
                            value={formData.discount_type}
                            onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                            className="w-full bg-zinc-800 border border-zinc-700 h-12 px-4 text-white"
                          >
                            <option value="percentage">Porcentagem (%)</option>
                            <option value="fixed">Valor Fixo (R$)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">
                            {formData.discount_type === 'percentage' ? 'Desconto (%)' : 'Desconto (R$)'} *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.discount_value}
                            onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                            className="w-full bg-zinc-800 border border-zinc-700 h-12 px-4 text-white"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Compra Mínima (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.min_purchase}
                          onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                          className="w-full bg-zinc-800 border border-zinc-700 h-12 px-4 text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Limite de Usos</label>
                        <input
                          type="number"
                          value={formData.max_uses}
                          onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                          className="w-full bg-zinc-800 border border-zinc-700 h-12 px-4 text-white"
                          placeholder="Ilimitado"
                        />
                      </div>

                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Data de Expiração</label>
                        <input
                          type="date"
                          value={formData.expires_at}
                          onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                          className="w-full bg-zinc-800 border border-zinc-700 h-12 px-4 text-white"
                        />
                      </div>

                      <div className="flex gap-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowModal(false)}
                          className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-3 uppercase tracking-wider transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="flex-1 bg-primary hover:bg-white text-black font-bold py-3 uppercase tracking-wider transition-colors"
                        >
                          Criar Cupom
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default AdminCoupons;